import * as React from "react";
import { useHistory, useParams, useRouteMatch } from "react-router";
import { URLS } from "@/utils";
import { PageWrapper } from "@/components/PageWrapper";
import { camelCase } from "lodash";
import { Overview } from "@/containers/Coach/Reputation/Overview";
import { Fans } from "@/containers/Coach/Reputation/Fans";
import { Forms } from "@/containers/Coach/Reputation/Forms";
import { PublicReviews } from "@/containers/Coach/Reputation/PublicReviews";
import { Reviews } from "@/containers/Coach/Reputation/Reviews";
import { reputationTabSearchMemory } from "./reputationTabSearchMemory";
import { ReputationHeader, ReputationTab } from "./components/ReputationHeader";
import { BarChart3, MessageSquare, Users, FileText, Globe, Star, Route } from "lucide-react";
import { cn } from "@/lib/utils";

const ReputationFC: React.FC = () => {
  const history = useHistory();
  const tabs = [
    { title: "Overview", key: "overview", component: Overview },
    { title: "Reviews", key: "reviews", component: Reviews },
    { title: "Fan Zone", key: "fans", component: Fans },
    { title: "Forms", key: "forms", component: Forms },
    { title: "Public Reviews", key: "publicReviews", component: PublicReviews },
  ];

  const params = useParams<{ tab?: string }>();
  // Form Routes is a page inside the Forms tab, so the Forms tab stays selected there
  const onFormRoutes = !!useRouteMatch({ path: URLS.coach.reputation.formRoutes, exact: true });
  const onPublicReviews = !!useRouteMatch({ path: URLS.coach.reputation.publicReviews, exact: true });
  const onLegacyWidgetTab = params.tab === "reviewWidget";
  const tab = onFormRoutes ? "forms" : onPublicReviews || onLegacyWidgetTab ? "publicReviews" : params.tab;
  const [selectedTabKey, setSelectedTabKey] = React.useState<string>(tab || tabs[0].key || "");

  // Layout effect so URL-driven tab switches (e.g. Overview metric → Reviews) unmount the
  // previous tab before its passive effects can rewrite search params.
  React.useLayoutEffect(() => {
    if (tab === "dashboard") {
      history.replace({
        pathname: URLS.coach.reputation.overview,
        search: "",
      });
      setSelectedTabKey("overview");
      return;
    }
    if (onLegacyWidgetTab) {
      history.replace(URLS.coach.reputation.publicReviewsWidget);
    }
    if (tab) setSelectedTabKey(tab);
  }, [tab, onLegacyWidgetTab, history]);

  const selectTab = (tabKey: string) => {
    if (selectedTabKey === "overview" || selectedTabKey === "reviews") {
      reputationTabSearchMemory[selectedTabKey] = history.location.search;
    }

    let search = "";
    if (tabKey === "overview" || tabKey === "reviews") {
      search = reputationTabSearchMemory[tabKey] || "";
    }

    history.replace({
      pathname: URLS.coach.reputation.tab.replace(":tab?", camelCase(tabKey)),
      search,
    });
    setSelectedTabKey(tabKey);
  };

  const TabComponent = React.useMemo(() => {
    return (tabs.find(tab => tab.key === selectedTabKey) || tabs[0]).component;
  }, [selectedTabKey]);

  const secondaryTabs: ReputationTab[] = [
    { key: "overview", title: "Overview", icon: BarChart3 },
    { key: "reviews", title: "Reviews", icon: MessageSquare },
    { key: "fans", title: "Fan Zone", icon: Users },
    { key: "forms", title: "Forms", icon: FileText },
    { key: "publicReviews", title: "Public Reviews", icon: Globe },
  ];

  const isScrollableTab = ["overview", "forms", "publicReviews"].includes(selectedTabKey);

  return (
    <PageWrapper hideSeparator fitViewport={true} className="reputation-management flex flex-col flex-1 min-h-0">
      <ReputationHeader
        selectedTabKey={selectedTabKey}
        onTabChange={selectTab}
        mainTabTitle="Reputation Manager"
        mainTabIcon={Star}
        tabs={secondaryTabs}
        subPage={
          onFormRoutes
            ? {
                title: "Form Routes",
                icon: Route,
                backTitle: "Back to Forms",
                onBack: () => history.push(URLS.coach.reputation.forms),
              }
            : undefined
        }
      />
      <div
        className={cn(
          "flex-1 min-h-0 h-full flex flex-col p-4",
          isScrollableTab ? "overflow-y-auto" : "overflow-hidden"
        )}
      >
        <TabComponent />
      </div>
    </PageWrapper>
  );
};

export const Reputation = ReputationFC;
